import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { RECEIPT_ACCEPT, formatFileSize } from "./addMoneyUtils";
import Styles from "./AddMoneyToUser.module.scss";

// Optional payment proof: click, press Enter/Space, or drop a file on the zone.
// Validation lives in the parent (`onPick`) so the rules stay in one place.
const ProofPicker = ({ file, onPick, onRemove, error, disabled, id }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file || !String(file.type).startsWith("image/")) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const openPicker = () => {
    if (!disabled && inputRef.current) inputRef.current.click();
  };

  const handleChange = (event) => {
    const picked = event.target.files && event.target.files[0];
    // Reset so choosing the same file again still fires onChange.
    event.target.value = "";
    if (picked) onPick(picked);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = event.dataTransfer.files && event.dataTransfer.files[0];
    if (dropped) onPick(dropped);
  };

  return (
    <Box>
      <input
        ref={inputRef}
        id={id}
        type="file"
        hidden
        accept={RECEIPT_ACCEPT}
        onChange={handleChange}
        disabled={disabled}
      />
      {file ? (
        <Box className={Styles.FileChip}>
          {previewUrl ? (
            <img src={previewUrl} alt="" className={Styles.Thumb} />
          ) : (
            <Box className={Styles.ThumbIcon}>
              <InsertDriveFileOutlinedIcon fontSize="small" />
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontFamily: "Inter", fontSize: "13px", fontWeight: 500, color: "#101828" }}>
              {file.name}
            </Typography>
            <Typography sx={{ fontFamily: "Inter", fontSize: "11.5px", color: "#667085" }}>
              {formatFileSize(file.size)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remove ${file.name}`}
            sx={{ color: "#667085" }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label="Attach proof of payment: browse or drop a JPG, PNG, GIF, WebP or PDF file"
          className={`${Styles.DropZone} ${dragging ? Styles.DropZoneActive : ""} ${
            disabled ? Styles.DropZoneDisabled : ""
          }`}
          onClick={openPicker}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openPicker();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <Box className={Styles.DropIcon}>
            <CloudUploadOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: "Inter", fontSize: "13px", fontWeight: 500 }}>
              Drop a file here or{" "}
              <Box component="span" sx={{ color: "#1E3A5F", textDecoration: "underline" }}>
                browse
              </Box>
            </Typography>
            <Typography sx={{ fontFamily: "Inter", fontSize: "11.5px", color: "#667085" }}>
              JPG, PNG, GIF, WebP or PDF · up to 5 MB
            </Typography>
          </Box>
        </Box>
      )}
      {error && (
        <Typography role="alert" sx={{ mt: 0.5, fontFamily: "Inter", fontSize: "11.5px", color: "#B42318" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ProofPicker;
